import * as i18n from "../i18n.ts";

/**
 * Localized commercial values shown on the offer and review screens.
 *
 * These are the amounts, currency, and renewal terms a customer sees before
 * checkout. They MUST come from an authoritative, per-locale backend
 * configuration (currency, amount due today, renewal price, tax, billing
 * period, renewal date) — never from hard-coded English/USD literals.
 *
 * Until that backend contract exists, every field resolves to a
 * currency-free placeholder string ("Nothing charged", "Price shown at
 * secure checkout"). This is deliberate: the tone guide forbids showing a
 * price we cannot honor, so we show a truthful "set at checkout" state
 * instead of an illustrative number.
 *
 * When the backend is wired, pass its localized values in as `overrides`;
 * this is the single seam a caller fills.
 */
export type CommercialValues = {
  /** Lead benefit line on the offer screen, e.g. the "today" cost. */
  offerPriceLine?: string;
  /** "Due today" amount on the review summary. */
  dueTodayValue?: string;
  /** Post-included-period renewal terms on the review summary. */
  renewalValue?: string;
};

export function getCommercialDisplay(overrides: CommercialValues = {}): Required<CommercialValues> {
  return {
    offerPriceLine: overrides.offerPriceLine
      || i18n.get("homehubActivation.commercial.offerPriceDefault"),
    dueTodayValue: overrides.dueTodayValue
      || i18n.get("homehubActivation.commercial.dueTodayDefault"),
    renewalValue: overrides.renewalValue
      || i18n.get("homehubActivation.commercial.renewalDefault")
  };
}
